/**
 * Hook dữ liệu của màn Phân quyền dữ liệu (ma trận theo bảng/cột và ma trận theo khuôn). Màn Cấu hình chatbot dùng lại
 * `useQuyenHieuLuc`, `useTaiKhoanQuyen`, `useMaTranKhuonNhieu` để dựng cảnh báo vai của bot nhúng.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { khoTroLy } from '../../../lib/repositories/tro-ly-ai.repository';
import type { KhuonGrant, KhuonMaTran, MaTranQuyen, PermGrant } from '../types';
import { chiPhanGuiLenCuaQuyen } from '../types';
import { KHOA, thuLai } from './useChung';

export const KHOA_QUYEN = {
  taiKhoan: [...KHOA.quyen, 'tai-khoan'] as const,
  maTran: (sourceId: string) => [...KHOA.quyen, 'ma-tran', sourceId] as const,
  hieuLuc: (vai: string) => [...KHOA.quyen, 'hieu-luc', vai] as const,
  maTranKhuon: (sourceId: string) => [...KHOA.khuon(sourceId), 'quyen'] as const,
};

/** Vai của người đăng nhập, tài khoản module liệt kê được, và `canManage` do máy chủ TÍNH — đừng tự suy. */
export function useTaiKhoanQuyen(bat = true) {
  return useQuery({
    queryKey: KHOA_QUYEN.taiKhoan,
    queryFn: khoTroLy.taiKhoanQuyen,
    staleTime: 60_000,
    retry: thuLai,
    enabled: bat,
  });
}

export function useMaTranQuyen(sourceId: string, bat: boolean) {
  return useQuery({
    queryKey: KHOA_QUYEN.maTran(sourceId),
    queryFn: () => khoTroLy.maTranQuyen(sourceId),
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat && !!sourceId,
  });
}

/** "Vai này đọc được gì" — bỏ trống `vai` là quyền của chính người đăng nhập. */
export function useQuyenHieuLuc(vai = '', bat = true) {
  return useQuery({
    queryKey: KHOA_QUYEN.hieuLuc(vai),
    queryFn: () => khoTroLy.quyenHieuLuc(vai || undefined),
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat,
  });
}

export function useMaTranKhuon(sourceId: string, bat = true) {
  return useQuery({
    queryKey: KHOA_QUYEN.maTranKhuon(sourceId),
    queryFn: () => khoTroLy.maTranKhuon(sourceId),
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat && !!sourceId,
  });
}

/** Ma trận khuôn của nhiều nguồn cùng lúc — cảnh báo vai nhúng chạy trên MỌI nguồn của bot, không chỉ nguồn đầu. */
export function useMaTranKhuonNhieu(sourceIds: readonly string[], bat: boolean) {
  return useQueries({
    queries: sourceIds.map((id) => ({
      queryKey: KHOA_QUYEN.maTranKhuon(id),
      queryFn: () => khoTroLy.maTranKhuon(id),
      staleTime: 30_000,
      retry: thuLai,
      enabled: bat,
    })),
  });
}

/**
 * Lưu xong thì làm mới CẢ nhánh `tro-ly-ai`: gói ngữ cảnh SQL, cảnh báo vai nhúng ở màn Cấu hình chatbot ("bot nhúng sẽ
 * KHÔNG đọc được số liệu") và quyền hiệu lực đều dựng từ ma trận này — giữ bản đệm cũ là để màn khác vẽ theo quyền vừa
 * bị thu hồi. Chỉ truy vấn đang hiện mới tải lại.
 */
export function useLuuMaTranQuyen(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grants: PermGrant[]) => khoTroLy.luuMaTranQuyen(sourceId, grants.map(chiPhanGuiLenCuaQuyen)),
    onSuccess: (_, grants) => {
      // Ghi ngay bản vừa lưu vào cache để bản nháp về `null` mà không nhấp nháy về bản cũ trong lúc chờ tải lại.
      qc.setQueryData<MaTranQuyen>(KHOA_QUYEN.maTran(sourceId), (cu) => (cu ? { ...cu, grants } : cu));
      return qc.invalidateQueries({ queryKey: KHOA.goc });
    },
  });
}

export function useLuuMaTranKhuon(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grants: KhuonGrant[]) => khoTroLy.luuMaTranKhuon(sourceId, grants),
    onSuccess: (_, grants) => {
      qc.setQueryData<KhuonMaTran>(KHOA_QUYEN.maTranKhuon(sourceId), (cu) => (cu ? { ...cu, grants } : cu));
      return qc.invalidateQueries({ queryKey: KHOA.goc });
    },
  });
}
